import { createRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { QuizEditor, type EditorQuiz } from '../../../components/QuizEditor'
import { Route as appRoute } from '../../_app'

/**
 * Create-a-quiz route. The page renders the shared `QuizEditor` with an
 * empty seed; submit fires two sequential API calls:
 *
 *   1. `POST /api/v1/quizzes` — creates the quiz record and returns the
 *      server-assigned id (the API ignores the questions array on
 *      create, hence the metadata-only payload at this step).
 *   2. `PUT /api/v1/quizzes/{id}` — replaces the quiz with the full
 *      questions list the host just edited.
 *
 * The two-step flow exists because T24's contract intentionally split
 * "create quiz shell" from "replace questions" so we don't validate
 * polymorphic question payloads twice. On success we invalidate the
 * `['quizzes']` list cache (so the list page reflects the new entry on
 * back-navigation) and route the host to `/quizzes/{id}/edit` — which
 * mirrors Kahoot's behaviour of staying on the editor after first save.
 */
export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/quizzes/new',
  component: NewQuizPage,
})

type CreateQuizResponse = {
  id: string
  title: string
  description: string
  questionCount: number
  updatedAt: string
}

type QuestionPayload = {
  text: string
  timeSeconds: number
  type: 'MultipleChoice' | 'TrueFalse' | 'OrderSequence'
  options?: string[]
  correctOptionIndex?: number
  correctAnswer?: boolean
  items?: string[]
  explanation?: string | undefined
}

const EMPTY_QUIZ: EditorQuiz = {
  title: '',
  description: '',
  questions: [],
}

function NewQuizPage() {
  const { t } = useTranslation('quiz')
  const queryClient = useQueryClient()

  const saveMutation = useMutation({
    mutationFn: async (quiz: EditorQuiz) => {
      const createRes = await fetch('/api/v1/quizzes', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: quiz.title,
          description: quiz.description,
        }),
      })
      if (!createRes.ok) {
        throw new Error(`Failed to create quiz: ${createRes.status}`)
      }
      const created = (await createRes.json()) as CreateQuizResponse

      // The PUT is only useful when the host already added questions
      // during the first session. Skipping it for empty quizzes saves
      // a round-trip and keeps the request log cleaner.
      if (quiz.questions.length > 0) {
        const updateRes = await fetch(
          `/api/v1/quizzes/${encodeURIComponent(created.id)}`,
          {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: quiz.title,
              description: quiz.description,
              questions: quiz.questions.map(toQuestionPayload),
            }),
          },
        )
        if (!updateRes.ok) {
          throw new Error(`Failed to save questions: ${updateRes.status}`)
        }
      }

      return created
    },
    onSuccess: (created) => {
      // Drop the cached list so the index page refetches on next visit.
      void queryClient.invalidateQueries({ queryKey: ['quizzes'] })
      // Full-page navigation rather than `useNavigate` because the
      // edit route did not exist when this file was last typechecked
      // (matches T22 / T25's cross-boundary `<a href>` pattern). The
      // freshly-regenerated `routeTree.gen.ts` resolves the target on
      // the next render after reload.
      window.location.assign(`/quizzes/${encodeURIComponent(created.id)}/edit`)
    },
  })

  return (
    <QuizEditor
      heading={t('newQuiz')}
      initialQuiz={EMPTY_QUIZ}
      onSave={async (quiz) => {
        await saveMutation.mutateAsync(quiz)
      }}
      isSaving={saveMutation.isPending}
      isError={saveMutation.isError}
    />
  )
}

/**
 * Coerce the editor's internal `EditorQuestion` discriminator into the
 * shape `PUT /api/v1/quizzes/{id}` accepts. Fields irrelevant to the
 * chosen type are intentionally omitted — the validator on the server
 * ignores them but they would noise up the JSON payload.
 */
function toQuestionPayload(
  q: EditorQuiz['questions'][number],
): QuestionPayload {
  if (q.type === 'MultipleChoice') {
    return {
      text: q.text,
      timeSeconds: q.timeSeconds,
      type: 'MultipleChoice',
      options: q.options,
      correctOptionIndex: q.correctOptionIndex,
      explanation: q.explanation?.trim() ? q.explanation.trim() : undefined,
    }
  }
  if (q.type === 'OrderSequence') {
    return {
      text: q.text,
      timeSeconds: q.timeSeconds,
      type: 'OrderSequence',
      items: q.items,
      explanation: q.explanation?.trim() ? q.explanation.trim() : undefined,
    }
  }
  return {
    text: q.text,
    timeSeconds: q.timeSeconds,
    type: 'TrueFalse',
    correctAnswer: q.correctAnswer,
    explanation: q.explanation?.trim() ? q.explanation.trim() : undefined,
  }
}
